"use client";

import { routes } from "@/config/routes";
import useApi from "@/hooks/useApi";
import PromotionRepository from "@/utilities/repositories/Promotion";
import { ICreatePromotionDTO, IPromotion } from "@/utilities/types/Promotion";
import { IResponseData } from "@/utilities/types/requests";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import PromotionForm from "./components/promotion-form";

export default function CreateEditPromotion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState<IPromotion | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { request: getPromotion } = useApi<IResponseData<IPromotion>>({
    request: PromotionRepository.getPromotion,
  });

  const { request: createPromotion, loading: creatingPromotion } = useApi({
    request: PromotionRepository.createPromotion,
  });

  const { request: updatePromotion, loading: updatingPromotion } = useApi({
    request: PromotionRepository.updatePromotion,
  });

  const isEditMode = !!id && id !== ":id";

  useEffect(() => {
    if (isEditMode) {
      fetchPromotionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPromotionDetails = async () => {
    try {
      setIsLoading(true);
      const response = await getPromotion(id as string);
      if (response?.data) {
        setInitialData(response.data);
      }
    } catch (error) {
      console.error("Error fetching promotion details", error);
      toast.error("Failed to fetch promotion details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: ICreatePromotionDTO) => {
    try {
      if (isEditMode && id) {
        const updateData: Partial<ICreatePromotionDTO> = {
          description: data.description,
          status: data.status,
          startDate: data.startDate,
          endDate: data.endDate,
          usageLimitType: data.usageLimitType,
          usageLimit: data.usageLimit,
          type: data.type,
        };

        await updatePromotion(id, updateData);
        toast.success("Promotion updated successfully");
      } else {
        await createPromotion(data);
        toast.success("Promotion created successfully");
      }

      navigate(routes.promotion.listing);
    } catch (error) {
      console.error("Error saving promotion", error);
      toast.error(
        isEditMode
          ? "Failed to update promotion"
          : "Failed to create promotion",
      );
      throw error;
    }
  };

  return (
    <div className="@container">
      <PromotionForm
        initialData={initialData}
        isLoading={isLoading || creatingPromotion || updatingPromotion}
        onSubmit={handleSubmit}
        isUpdate={isEditMode}
      />
    </div>
  );
}
